<template>
  <v-data-table
    :headers="headers"
    :items="rows"
    class="px-5"
    :loading="loading.load"
    loading-text="Загрузка... Пожалуйста подождите"
    :mobile-breakpoint="300"
    :page="filter.page"
    :items-per-page="filter.page_size"
    :height="tableHeight"
    fixed-header
    hide-default-footer
    disable-sort
    :server-items-length="count"
  >
    <template v-slot:top>
      <v-toolbar flat height="auto" class="py-3">
        <div class="d-flex flex-wrap" style="gap: 12px; width: 100%">
          <v-text-field
            label="Компания" append-icon="mdi-magnify" class="font-sm" :value="filter.company"
            @input="debouncedSetFilter('company', $event)" dense hide-details outlined style="max-width: 220px"
          />
          <v-text-field
            label="Email" append-icon="mdi-magnify" class="font-sm" :value="filter.email"
            @input="debouncedSetFilter('email', $event)" dense hide-details outlined style="max-width: 220px"
          />
          <v-text-field
            label="ИНН" append-icon="mdi-magnify" class="font-sm" :value="filter.taxpayer_id_number"
            @input="debouncedSetFilter('taxpayer_id_number', $event)" dense hide-details outlined style="max-width: 180px"
          />
          <v-select
            label="Статус кабинета" class="font-sm" :value="filter.activity_status || null"
            :items="activityStatuses"
            @change="setFilterValue({ key: 'activity_status', value: $event || '' })"
            clearable dense hide-details outlined style="max-width: 200px"
          />
          <v-text-field
            label="Активен с" type="date" class="font-sm" :value="filter.active_until_from"
            @input="onUntilFilter('active_until_from', $event)" clearable dense hide-details outlined style="max-width: 190px"
          />
          <v-text-field
            label="Активен по" type="date" class="font-sm" :value="filter.active_until_to"
            @input="onUntilFilter('active_until_to', $event)" clearable dense hide-details outlined style="max-width: 190px"
          />
          <CorpTagsSelect
            v-if="tagsAvailable"
            label="Теги" class="font-sm" :value="filter.tags"
            hide-details style="max-width: 260px"
            @input="setFilterValue({ key: 'tags', value: $event })"
          />
          <v-select
            v-if="isAdmin"
            label="Супервизор" class="font-sm" :value="filter.supervisor || null"
            :items="supervisors" :item-text="partnerName" item-value="id"
            @change="setFilterValue({ key: 'supervisor', value: $event || '' })"
            clearable dense hide-details outlined style="max-width: 240px"
          />
        </div>
      </v-toolbar>
    </template>

    <template v-slot:item.company="{ item }">
      <router-link
        v-if="$store.getters['auth/isAllowed']('view_corpclient')"
        class="text-primary"
        :to="{ name: 'clients-id', params: { id: item.id } }"
      >
        {{ item.company || '—' }}
      </router-link>
      <span v-else>{{ item.company || '—' }}</span>
    </template>

    <template v-slot:item.balance="{ item }">
      <span v-if="item.stats" :class="{ 'error--text': item.stats.totals.balance < 0 }">{{ item.stats.totals.balance }}</span>
      <!-- Сводки нет — показываем старый счётчик, если бэк его прислал -->
      <span v-else-if="item.stock_balance != null">{{ item.stock_balance }}</span>
      <span v-else>—</span>
    </template>

    <template v-slot:item.buffer="{ item }">
      <span v-if="item.stats" :class="{ 'error--text': item.stats.bufferBalance < 0 }">{{ item.stats.bufferBalance }}</span>
      <span v-else>—</span>
    </template>

    <template v-slot:item.spent="{ item }">
      {{ item.stats ? item.stats.totals.spent : '—' }}
    </template>

    <template v-slot:item.activity="{ item }">
      <CorpActivityChip :source="item" dash />
    </template>

    <template v-slot:item.tags="{ item }">
      <CorpTagChips :tags="item.tags" dash />
    </template>

    <template v-slot:item.partner="{ item }">
      {{ partnerLinkName(item.supervisor) }}
    </template>

    <template v-slot:item.referrer="{ item }">
      {{ partnerLinkName(item.referrer) }}
    </template>

    <template v-slot:item.contact="{ item }">
      <span class="grey--text text--darken-1">{{ contactName(item) }}</span>
    </template>

    <template v-slot:item.id="{ item }">
      <span class="caption grey--text">{{ item.id }}</span>
    </template>

    <template v-slot:no-data> Корпоративных клиентов не найдено. </template>

    <template v-slot:footer="{ props }">
      <v-data-footer
        :pagination="props.pagination"
        :options="props.options"
        items-per-page-text="Клиентов на странице:"
        :items-per-page-options="[10, 25, 50, 100]"
        :page-text="`Всего клиентов: ${props.pagination.itemsLength}. Страница ${props.options.page} из ${props.pagination.pageCount}`"
        show-current-page
        show-first-last-page
        @update:options="updateOptions"
      />
    </template>
  </v-data-table>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import api from '@/common/api';
import { ADMIN } from '@/common/constants/userTypes';
import { parseWalletStatistics } from '@/common/models/walletStatistics';
import { PARTNER_TYPES, isPartnerOfType, partnerIdOf } from '@/common/models/partnerTypes';
import { ACTIVITY_STATUS_OPTIONS } from '@/common/models/corpActivity';
import CorpActivityChip from '@/components/clients/card/CorpActivityChip.vue';
import CorpTagChips from '@/components/clients/tags/CorpTagChips.vue';
import CorpTagsSelect from '@/components/clients/tags/CorpTagsSelect.vue';

const fullName = (person) => [person.last_name, person.first_name].filter(Boolean).join(' ');

export default {
  name: 'ClientsTable',
  components: { CorpActivityChip, CorpTagChips, CorpTagsSelect },
  data: () => ({
    activityStatuses: ACTIVITY_STATUS_OPTIONS,
    tableHeight: 400,
    // Свой список: в сторе users/partners лежит список с фильтрами страницы «Партнёры».
    partners: [],
  }),
  computed: {
    ...mapGetters({
      loading: 'users/clients/loading',
      clients: 'users/clients/clients',
      count: 'users/clients/count',
      filter: 'users/clients/filter',
      user: 'auth/user',
      tagsAvailable: 'users/corpClientTags/available',
    }),
    isAdmin() {
      return !!this.user && this.user.user_type === ADMIN;
    },
    supervisors() {
      return this.partners.filter((p) => isPartnerOfType(p, PARTNER_TYPES.SUPERVISOR));
    },
    headers() {
      return [
        { text: 'ID', value: 'id', width: '70px' },
        { text: 'Компания', value: 'company' },
        { text: 'Email', value: 'email' },
        { text: 'Общий остаток', value: 'balance', align: 'end' },
        { text: 'В буфере', value: 'buffer', align: 'end' },
        { text: 'Израсходовано за 30 дней', value: 'spent', align: 'end' },
        { text: 'Кабинет активен до', value: 'activity' },
        ...(this.isAdmin ? [{ text: 'Супервизор', value: 'partner' }, { text: 'Реферал', value: 'referrer' }] : []),
        ...(this.tagsAvailable ? [{ text: 'Теги', value: 'tags' }] : []),
        { text: 'Менеджер', value: 'contact' },
      ];
    },
    rows() {
      // Остатки и обороты — только через адаптер; если сводки нет, в ячейках «—», а не нули.
      return this.clients.map((client) => ({ ...client, stats: parseWalletStatistics(client.wallet_statistics) }));
    },
  },
  methods: {
    ...mapActions({
      loadClientsFilter: 'users/clients/loadClientsFilter',
      setFilterPageSize: 'users/clients/setFilterPageSize',
      setFilterValue: 'users/clients/setFilterValue',
      ensureTags: 'users/corpClientTags/ensureLoaded',
    }),
    updateOptions(param) {
      this.setFilterPageSize({ page: param.page, pageSize: param.itemsPerPage });
    },
    // Отдельный debounce на каждое поле: общий терял бы фильтр, если быстро перейти к соседнему полю.
    debouncedSetFilter(key, value) {
      if (!this.filterDebouncers[key]) {
        this.filterDebouncers[key] = debounce((val) => this.setFilterValue({ key, value: val }), 500);
      }
      this.filterDebouncers[key](value);
    },
    // Диапазон «Активен до»: пустое значение сбрасывает фильтр, перевёрнутый диапазон на сервер не отправляем
    onUntilFilter(key, value) {
      const next = value || '';
      const from = key === 'active_until_from' ? next : this.filter.active_until_from;
      const to = key === 'active_until_to' ? next : this.filter.active_until_to;
      if (from && to && from > to) {
        this.$toast.warning('Дата «с» позже даты «по»', '', { position: 'topRight' });
        return;
      }
      this.debouncedSetFilter(key, next);
    },
    partnerName(partner) {
      return fullName(partner) || 'имя не задано';
    },
    // Привязка корпа: числовой ID партнёра, null или вложенный объект
    partnerLinkName(link) {
      const id = partnerIdOf(link);
      if (!id) return '—';
      const partner = typeof link === 'object' ? link : this.partners.find((p) => String(p.id) === String(id));
      return partner ? this.partnerName(partner) : '—';
    },
    async loadPartners() {
      try {
        const response = await api.get('/users/partners/', { params: { page_size: -1 } });
        this.partners = Array.isArray(response) ? response : (response.results || []);
      } catch (e) {
        this.partners = [];
      }
    },
    contactName(item) {
      return fullName(item) || '—';
    },

  },
  created() {
    this.filterDebouncers = {};
    this.loadClientsFilter();
    if (this.isAdmin) {
      this.loadPartners();
      this.ensureTags();
    }
  },
  mounted() {
    const height = document.documentElement.clientHeight - 270;
    this.tableHeight = Math.max(height, this.tableHeight);
  }
};
</script>
  <style lang="scss">
.v-list.v-sheet {
  padding: 0 !important;
}

.v-list-item__content {
  padding: 0 !important;
}

.v-list-item__action {
  margin-top: 0;
  margin-bottom: 0;
}

.v-select__selections {
  flex-wrap: nowrap;
}

.other-width-col {
  width: 13rem;
}
</style>
